import { memo, useCallback, useEffect, useRef } from 'react'

import { MediaChannelEdit } from '@nativewaves/nw-cloud-sdk/media'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useMediaChannelEntryUpdateMutation } from 'hooks/mutations/publishing'
import { mediaChannelEntryIndexRoute } from 'pages/NWPlatform/MediaChannels/MediaChannel/MediaChannelEntry'
import { mediaChannelQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { mediaChannelId, mediaChannelEntryId } = mediaChannelEntryIndexRoute.useParams()

  const mediaChannelEntryQuery = useQuery(
    mediaChannelQueryKeys.entryDetail({
      channelId: mediaChannelId,
      entryId: mediaChannelEntryId,
    }),
  )
  const { data: mediaChannelEntry } = mediaChannelEntryQuery

  const { mutate: updateChannelEntry } = useMediaChannelEntryUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: MediaChannelEdit) => {
      updateChannelEntry(
        { channelId: mediaChannelId, entryId: mediaChannelEntryId, mediaChannelScheduleEntryEdit: data },
        {
          onSuccess: () => {
            enqueueSnackbar(t('domain:MediaChannel.mediaChannelEntrySaved'), { variant: 'success' })
          },
        },
      )
    },
    [enqueueSnackbar, mediaChannelEntryId, mediaChannelId, t, updateChannelEntry],
  )

  useEffect(() => {
    if (editorInstanceRef.current && mediaChannelEntryQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(mediaChannelEntry, null, 4))
    }
  }, [mediaChannelEntryQuery.isSuccess, mediaChannelEntry])

  if (mediaChannelEntryQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!mediaChannelEntry) {
    return <NotFound />
  }

  return (
    <Editor
      value={mediaChannelEntry}
      onSubmit={handleFormSubmit}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
